import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, priorityChip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Play, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee: "", priority: "medium", status: "todo", due_date: "" });
  const [timerStart, setTimerStart] = useState(null);
  const [timerNote, setTimerNote] = useState("");

  // Bucket tasks by status once per tasks change (avoids re-filtering per column each render).
  const tasksByStatus = useMemo(() => {
    const groups = { todo: [], in_progress: [], done: [] };
    for (const t of tasks) {
      if (groups[t.status]) groups[t.status].push(t);
    }
    return groups;
  }, [tasks]);

  const load = () => {
    api.get("/tasks").then(r => setTasks(r.data));
    api.get("/time-entries").then(r => setEntries(r.data));
    api.get("/users").then(r => setUsers(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/tasks", form);
    toast.success("Task created");
    setOpen(false);
    setForm({ title: "", description: "", assignee: "", priority: "medium", status: "todo", due_date: "" });
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  const del = async (id) => {
    await api.delete(`/tasks/${id}`);
    load();
  };

  const startTimer = () => setTimerStart(Date.now());
  const stopTimer = async () => {
    if (!timerStart) return;
    const durationMin = Math.max(1, Math.round((Date.now() - timerStart) / 60000));
    await api.post("/time-entries", { note: timerNote || "Untitled entry", minutes: durationMin, user_id: user.id, user_name: user.name });
    toast.success(`Logged ${durationMin} minute(s)`);
    setTimerStart(null); setTimerNote("");
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks & Time"
        subtitle="Assign work, track time, hit deadlines"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="task-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
                </div>
                <div><Label>Assign to</Label>
                  <Select value={form.assignee} onValueChange={v => setForm({...form, assignee: v})}>
                    <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-task-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="time">Time Tracking</TabsTrigger>
        </TabsList>
        <TabsContent value="tasks" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["todo", "in_progress", "done"].map(s => (
              <div key={s} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-semibold text-sm text-slate-800 capitalize">{s.replace("_", " ")}</div>
                  <span className="text-xs num text-slate-400">{tasksByStatus[s].length}</span>
                </div>
                <div className="space-y-2">
                  {tasksByStatus[s].map(t => (
                    <div key={t.id} className="bg-slate-50 border border-slate-100 rounded-md p-3 group" data-testid={`task-card-${t.id}`}>
                      <div className="flex justify-between gap-2">
                        <div className="text-sm font-medium text-slate-800">{t.title}</div>
                        <button onClick={() => del(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Chip variant={priorityChip(t.priority)}>{t.priority}</Chip>
                        {t.due_date && <span className="text-[11px] text-slate-500">Due {formatDate(t.due_date)}</span>}
                      </div>
                      <div className="mt-2 flex gap-1">
                        {["todo","in_progress","done"].filter(x => x !== t.status).map(x => (
                          <Button key={x} size="sm" variant="ghost" onClick={() => updateStatus(t.id, x)} className="text-xs h-6 px-2 capitalize">
                            → {x.replace("_"," ")}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="time" className="mt-4 space-y-4">
          <div className="kpi-card">
            <div className="font-heading font-semibold text-lg text-slate-900 mb-3">Timer</div>
            <div className="flex gap-2">
              <Input placeholder="What are you working on?" value={timerNote} onChange={e => setTimerNote(e.target.value)} data-testid="timer-note-input" />
              {!timerStart ? (
                <Button onClick={startTimer} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="start-timer-btn">
                  <Play className="w-4 h-4 mr-1" /> Start
                </Button>
              ) : (
                <Button onClick={stopTimer} variant="destructive" data-testid="stop-timer-btn">
                  <Square className="w-4 h-4 mr-1" /> Stop & log
                </Button>
              )}
            </div>
            {timerStart && <div className="text-xs text-slate-500 mt-2 num">Started {new Date(timerStart).toLocaleTimeString()}</div>}
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-right px-4 py-3">Minutes</th>
                  <th className="text-left px-4 py-3">Logged</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} className="row">
                    <td className="px-4 py-3 text-slate-800">{e.note}</td>
                    <td className="px-4 py-3 text-slate-600">{e.user_name}</td>
                    <td className="px-4 py-3 text-right num">{e.minutes}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(e.created_at)}</td>
                  </tr>
                ))}
                {entries.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500 text-sm">No time entries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
